import { FormContext } from "@agtech/core/forms/AgTechFormContext"
import { AgTechFormControlUpdate } from "@agtech/core/forms/AgTechFormControls"
import { CSSProperties, useContext, useEffect, useRef, useState } from "react"
import { useAgTechState } from "@agtech/core/utilities/AgTechUtilities"
import { AgTechQueryRun, useAgTechQueryHandler } from "@agtech/core/data/queries/AgTechDataQueries"
import { IAgTechSelectionItem, AgTechSelectionControlContentProps, AgTechSelectionControlItemCreationProps, useAgTechSelectionPopUp } from "../Selection/AgTechCoreSelectionControlData"
import { AgTechSelectionListHeader, AgTechSelectionListBody } from "../Selection/AgTechSelectionPopUp"

export type AgTechFormLookupProps<TFormEntity, TItem extends IAgTechSelectionItem> = {
    entityType: TFormEntity,
    selection: AgTechFormLookupSelectionProps<TFormEntity, TItem>,
    control?: AgTechFormLookupControlProps,
    customSelectionContent?: AgTechSelectionControlContentProps<TItem>,
    style?: 'default' | 'grid'
}

export type AgTechFormLookupStyle = 'default' | 'grid';

export type AgTechFormLookupSelectionProps<TFormEntity, TItem extends IAgTechSelectionItem> = {
    selectionControlTitle?: string,
    data: {
        itemQuery: AgTechQueryRun<TItem[]>,
        selectedItemId: string | undefined,
        searchItems: (item: TItem, filter: string) => boolean,
        filterItems?: (item: TItem) => boolean,
    },
    behavior: AgTechFormLookupBehaviorProps<TFormEntity, TItem>,
    items: AgTechFormLookupItemProps<TItem>,
    popup?: AgTechFormLookupPopupProps
}

export type AgTechFormLookupItemProps<TItem extends IAgTechSelectionItem> = {
    itemText: (item: TItem) => string,
    itemHeight: number,
    itemRendering: (item: TItem) => React.ReactNode,
    itemCreationConfig?: AgTechSelectionControlItemCreationProps<TItem>,
};

export type AgTechFormLookupBehaviorProps<TFormEntity, TItem extends IAgTechSelectionItem> = {
    onItemSelected: AgTechFormControlUpdate<TFormEntity, TItem | undefined>,
    onBlur?: (text: string) => void,
    freeText?: {
        getFreeTextMessage?: (text: string) => string,
        onFreeTextEntered: AgTechFormControlUpdate<TFormEntity, string>
    }
}

export type AgTechFormLookupPopupProps = {
    height?: number,
    width?: number,
    styles?: CSSProperties
}

export type AgTechFormLookupControlProps = {
    caption?: string,
    readonlyIf?: (val: any) => boolean,
    isRequired?: boolean,
    showAsRequired?: boolean,
    width?: number,
    tabIndex?: number,
    inputClasses?: string,
    captionClasses?: string,
    containerClasses?: string,
    containerStyles?: CSSProperties,
    controlClasses?: string,
}

type AgTechFormLookupControlState<TItem extends IAgTechSelectionItem> = {
    items: TItem[],
    searchFilter: string,
    selectedItemText: string,
    isOpen: boolean,
    freeText?: string
}

const AgTechFormLookup = <TFormEntity, TSelectionItem extends IAgTechSelectionItem>(
    props: AgTechFormLookupProps<TFormEntity, TSelectionItem>
) => {
    const { 
        selectionInputRef, 
        selectionState, 
        setSelectionState, 
        handleItemCleared
    } = useAgTechFormLookupHandling(props);

    const lookupStyle = props.style ?? 'default';

    const selectionValueActiveClass = selectionState.current.isOpen ? 'active' : '';
    const selectionValueFilteredClass = selectionState.current.searchFilter ? 'filtered' : '';

    const shouldShowAsRequired = props.control?.isRequired && (props.control.showAsRequired ?? true);
    const formControlWidth: string = `${props.control?.width ?? 50}%`;
    const supportsFreetext = props.selection.behavior.freeText !== undefined;

    const selectedItemText = selectionState.current.selectedItemText
        ? selectionState.current.selectedItemText
        : supportsFreetext ? selectionState.current.freeText : '';

    return lookupStyle === 'default' ? (
        <div className={`agtech-form-control ${props.control?.containerClasses ?? ''}`} style={{ maxWidth: formControlWidth, ...props.control?.containerStyles }}>
            { props.control?.caption ? (
                <h1 className={`agtech-form-control-caption ${props.control.captionClasses ?? ''}`}>{props.control.caption + (shouldShowAsRequired ? ' *' : '')}</h1>
            ) : null }
            <div className={`selection-form-control ${props.control?.controlClasses ?? ''}`}>
                <div className={`selection-form-control-value ${selectionValueActiveClass} ${selectionValueFilteredClass}`}>
                    {selectedItemText}
                </div>
                <input
                    ref={selectionInputRef}
                    className={`selection-form-control-search ${props.control?.inputClasses ?? ''}`}
                    aria-autocomplete="list"
                    autoComplete="off"
                    type='text'
                    tabIndex={props.control?.tabIndex}
                    value={selectionState.current.searchFilter}
                    onChange={e => {
                        setSelectionState(curr => ({
                            ...curr,
                            searchFilter: e.target.value
                        }));
                    }}
                />
                {!props.control?.isRequired && 
                  props.selection.data.selectedItemId &&
                  props.selection.data.selectedItemId !== '0' ? 
                    <i className="fas fa-times selection-form-control-icon" onClick={handleItemCleared} /> : 
                    <i className="fas fa-caret-down selection-form-control-icon" /> }
            </div>
        </div>
    ) : (
        <div className='relative stretched border border-transparent flex'>
            <div className={`relative px-3 stretched items-center flex ${selectionValueActiveClass} ${selectionValueFilteredClass}`}>
                {selectedItemText}
            </div>
            <input
                ref={selectionInputRef}
                className='stretched absolute border-0 outline-none px-3 bg-transparent z-[2] flex'
                aria-autocomplete="list"
                autoComplete="off"
                type='text'
                value={selectionState.current.searchFilter}
                onChange={e => {
                    setSelectionState(curr => ({
                        ...curr,
                        searchFilter: e.target.value
                    }));
                }}
                tabIndex={props.control?.readonlyIf ? -1 : 0}
            />
        </div>
    )
}

export const useAgTechFormLookupHandling = <TFormEntity, TSelectionItem extends IAgTechSelectionItem>(
    props: AgTechFormLookupProps<TFormEntity, TSelectionItem>
) => {
    const formContext = useContext(FormContext);
    const selectionInputRef = useRef<HTMLInputElement>(null);

    const [selectionState, setSelectionState] = useAgTechState<AgTechFormLookupControlState<TSelectionItem>>({
        items: [],
        searchFilter: '',
        selectedItemText: '',
        isOpen: false
    });

    const selectionPopup = props.selection.popup ?? {
        height: 260,
        width: 320
    };

    useAgTechQueryHandler({
        query: props.selection.data.itemQuery,
        onQuerySuccess: async items => {
            const selectedItem = items.find(i => i.id.toString() === props.selection.data.selectedItemId);

            setSelectionState(curr => ({
                ...curr,
                items: items,
                selectedItemText: selectedItem ? props.selection.items.itemText(selectedItem) : ''
            }));
        }
    });

    useEffect(() => {       
        setSelectionState(curr => {
            const selectedItem = curr.items.find(i => i.id.toString() === props.selection.data.selectedItemId);

            return {
                ...curr,
                selectedItemText: selectedItem ? props.selection.items.itemText(selectedItem) : ''
            };
        });
    }, [props.selection.data.selectedItemId]);

    const handleItemCleared = async (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();

        if (formContext.entity) {
            await formContext.handleFieldChanged(undefined, props.selection.behavior.onItemSelected);
        }
        else {
            await props.selection.behavior.onItemSelected(formContext.entity, undefined);
        }

        setSelectionState(curr => ({
            ...curr,
            selectedItemText: '',
            isOpen: false,
            searchFilter: '',
            freeText: undefined
        }));
    }

    useAgTechSelectionPopUp({
        selectionRef: selectionInputRef,
        selectionProps: {
            items: {
                itemsQuery: props.selection.data.itemQuery,
                getSelectedItemId: () => props.selection.data.selectedItemId,
                onItemSelected: async selectedItem => {
                    if (formContext.entity) {
                        await formContext.handleFieldChanged(
                            selectedItem,
                            props.selection.behavior.onItemSelected);
                    }
                    else {
                        await props.selection.behavior.onItemSelected(
                            formContext.entity,
                            selectedItem);
                    }
        
                    setSelectionState(curr => {
                        const updatedItems = curr.items.some(i => i.id === selectedItem.id)
                            ? curr.items
                            : [...curr.items, selectedItem];

                        return {
                            ...curr,
                            items: updatedItems,
                            selectedItemText: props.selection.items.itemText(selectedItem),
                            freeText: undefined,
                            searchFilter: ''
                        };
                    });
                },
                searchItems: props.selection.data.searchItems,
                filterItems: props.selection.data.filterItems,
                itemCreationConfig: props.selection.items.itemCreationConfig,
                freeText: selectionState.current.freeText
            },
            content: props.customSelectionContent ?? {
                header: headerProps => (
                    <AgTechSelectionListHeader
                        {...headerProps}
                        title={props.selection.selectionControlTitle ?? props.control?.caption}
                        creationConfig={props.selection.items.itemCreationConfig}
                    />
                ),
                body: bodyProps => (
                    <AgTechSelectionListBody
                        {...bodyProps}
                        itemHeight={props.selection.items.itemHeight} 
                        item={props.selection.items.itemRendering}
                    />
                ),
                height: selectionPopup.height ?? 260,
                width: selectionPopup.width ?? selectionInputRef.current?.offsetWidth ?? 320,
                styles: selectionPopup.styles
            },
            isOpen: selectionState.current.isOpen
        },
        onPopupOpened: () => setSelectionState(curr => ({
            ...curr,
            searchFilter: curr.freeText ?? '',
            isOpen: true
        })),
        onPopupClosed: () => {
            const supportsFreeText = props.selection.behavior.freeText !== undefined;

            if (supportsFreeText) {
                const enteredText = selectionState.current.searchFilter;
                const selectedItemText = selectionState.current.selectedItemText;

                const isEnteredTextDifferentThanSelected = selectedItemText && enteredText && selectedItemText !== enteredText;
                const isEnteredTextNotEmpty = enteredText !== '';

                if (isEnteredTextDifferentThanSelected || isEnteredTextNotEmpty) {
                    formContext.handleFieldChanged(
                        enteredText,
                        props.selection.behavior.freeText!.onFreeTextEntered);
                }
            }

            setSelectionState(curr => {
                return ({
                    ...curr,
                    searchFilter: '',
                    isOpen: false,
                    freeText: curr.searchFilter
                });
            });
        },
        onPopUpBlurred: () => {
            props.selection.behavior.onBlur?.(selectionState.current.searchFilter);
        }
    });

    return {
        selectionInputRef,
        selectionState,
        setSelectionState,
        handleItemCleared
    }
}

export default AgTechFormLookup;